'use client';

import { useState, type JSX } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface CompanyBasicInfoProps {
  formData: {
    companyName: string;
    registrationNumber: string;
    foundingDate: string;
    companyType: string;
    taxNumber: string;
    [key: string]: string | boolean;
  };
  onChange: (section: string, field: string, value: string | boolean) => void;
}

export default function CompanyBasicInfo({
  formData,
  onChange,
}: Readonly<CompanyBasicInfoProps>): JSX.Element {
  const { t } = useLanguage();
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: string): string | null => {
    let error: string | null = null;

    if (value.trim() === '') {
      return null; // Empty validation happens elsewhere
    }

    switch (field) {
      case 'companyName':
        if (value.length < 3) {
          error = t.nameMinLength;
        } else if (value.length > 100) {
          error = t.nameMaxLength;
        }
        break;
      case 'registrationNumber':
        // Simple validation for registration number
        if (!/^[0-9A-Za-z-]+$/.test(value)) {
          error = t.invalidRegNumber;
        }
        break;
      case 'foundingDate':
        // Check if date is in the future
        if (value && new Date(value) > new Date()) {
          error = t.futureDateError;
        }
        break;
      case 'taxNumber':
        if (!/^[0-9A-Za-z-]+$/.test(value)) {
          error = t.invalidTaxNumber;
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const markFieldAsDirty = (field: string): void => {
    setDirtyFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field: string): void => {
    markFieldAsDirty(field);
    const value = formData[field];
    if (typeof value === 'string') {
      validateField(field, value);
    }
  };

  const shouldShowError = (field: string): boolean => {
    return Boolean(dirtyFields[field] && errors[field]);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">{t.companyBasicInformation}</h3>
      <p className="text-sm text-gray-500">{t.provideBasicInfo}</p>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="companyName" className="flex items-center">
            {t.companyName} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="companyName"
            placeholder={t.companyNamePlaceholder}
            value={formData.companyName}
            onChange={(e) => onChange('basic', 'companyName', e.target.value)}
            onBlur={() => handleBlur('companyName')}
            className={shouldShowError('companyName') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('companyName') && (
            <p className="text-sm text-red-500">{errors.companyName}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.companyNameHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="registrationNumber" className="flex items-center">
            {t.registrationNumber} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="registrationNumber"
            placeholder={t.registrationNumberPlaceholder}
            value={formData.registrationNumber}
            onChange={(e) => onChange('basic', 'registrationNumber', e.target.value)}
            onBlur={() => handleBlur('registrationNumber')}
            className={shouldShowError('registrationNumber') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('registrationNumber') && (
            <p className="text-sm text-red-500">{errors.registrationNumber}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.registrationNumberHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="foundingDate" className="flex items-center">
            {t.foundingDate} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="foundingDate"
            type="date"
            value={formData.foundingDate}
            onChange={(e) => onChange('basic', 'foundingDate', e.target.value)}
            onBlur={() => handleBlur('foundingDate')}
            className={shouldShowError('foundingDate') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('foundingDate') && (
            <p className="text-sm text-red-500">{errors.foundingDate}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.foundingDateHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyType" className="flex items-center">
            {t.companyType} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Select
            value={formData.companyType}
            onValueChange={(value) => onChange('basic', 'companyType', value)}
            required
          >
            <SelectTrigger
              id="companyType"
              className={shouldShowError('companyType') ? 'border-red-500' : ''}
              onBlur={() => handleBlur('companyType')}
            >
              <SelectValue placeholder={t.companyTypePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="llc">{t.llc}</SelectItem>
              <SelectItem value="corporation">{t.corporation}</SelectItem>
              <SelectItem value="partnership">{t.partnership}</SelectItem>
              <SelectItem value="soleProprietorship">{t.soleProprietorship}</SelectItem>
              <SelectItem value="nonprofit">{t.nonprofit}</SelectItem>
            </SelectContent>
          </Select>
          {shouldShowError('companyType') && (
            <p className="text-sm text-red-500">{errors.companyType}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.companyTypeHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="taxNumber" className="flex items-center">
            {t.taxNumber} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="taxNumber"
            placeholder={t.taxNumberPlaceholder}
            value={formData.taxNumber}
            onChange={(e) => onChange('basic', 'taxNumber', e.target.value)}
            onBlur={() => handleBlur('taxNumber')}
            className={shouldShowError('taxNumber') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('taxNumber') && (
            <p className="text-sm text-red-500">{errors.taxNumber}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.taxNumberHelp}</p>
        </div>
      </div>
    </div>
  );
}
